/**
 * Общая механика адаптеров канона: «именованный набор → готовый ответ сервера».
 *
 * Экран Astra спрашивает набор по ИМЕНИ ФАЙЛА прототипа (`data/loader.ts`,
 * `FILES`). Это имя и есть контракт: пока оно живо, компонент не переписывается
 * при смене источника. Адаптер обязан вернуть ТУ ЖЕ ФОРМУ, что лежала в файле,
 * собрав её из канонических эндпоинтов, — расхождения смысла закрываются
 * словами на экране, а не подгонкой чисел.
 *
 * Читатель (`DatasetReader`) возвращает `null`, если имя не его. Так каждый
 * срез переноса добавляет СВОЙ файл адаптеров и ровно одну строку регистрации
 * в `data-source/api.ts`: общие файлы не становятся местом встречи всех веток.
 *
 * ЧТО ЗДЕСЬ ЗАПРЕЩЕНО. Адаптер не пересчитывает журнал обязательств в
 * браузере: на канонических объёмах это не «медленнее», а неверно — выборка
 * приходит страницами, и свёртка по неполной странице выглядела бы полным
 * итогом. Считает сервер, адаптер только переименовывает поля.
 */

import { ApiError, type QueryParams } from '../../../api/client';
import { VersionedMemo } from '../../../api/dataEpoch';
import { onPrincipalChange } from '../../../api/session';
import type { ReadResult } from '../types';

/**
 * Читатель набора. `null` — «имя не моё»: список читателей перебирается по
 * очереди, и отсутствие владельца имени обязано отличаться от отказа.
 */
export type DatasetReader = (
  name: string,
  params: QueryParams,
) => Promise<ReadResult<unknown>> | null;

/**
 * Гектары из микрогектаров канона.
 *
 * `null` — «не измерено», и оно остаётся `null`: подставить здесь ноль значило
 * бы утверждать нулевую площадь там, где источник не сказал ничего (инвариант
 * «отсутствие ≠ ноль»). Поля прототипа, у которых тип — число, берут
 * `haOrOmit` и обязаны сказать рядом словами, что в сумму не вошло.
 */
export function toHa(microha: number | null | undefined): number | null {
  return microha === null || microha === undefined ? null : microha / 1_000_000;
}

/**
 * Площадь для поля прототипа, обязанного быть числом. Неназванное в сумму НЕ
 * входит: ноль здесь — форма поля, а не утверждение, и строка помечается
 * `area_stated: false` либо оговаривается словами на экране.
 */
export function haOrOmit(microha: number | null | undefined): number {
  return toHa(microha) ?? 0;
}

/** Число из десятичной строки канона; `null` остаётся `null`. */
export function decimal(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Координата, которой в каноне нет.
 *
 * Центр массива и контура прототип хранил рядом со строкой реестра; канон
 * геометрию отдаёт отдельным слоем (`/map/*`). Подставить сюда нули значило бы
 * поставить весь район в Гвинейский залив, поэтому поле заполняется значением,
 * которое нельзя принять за место. Ни один экран Astra центр не читает —
 * поле живёт только ради формы файла.
 */
export const NO_CENTER: [number, number] = [Number.NaN, Number.NaN];

/** Отказ словами: причина едет на экран, а не в консоль. */
export function failure(reason: string): ReadResult<never> {
  return { ok: false, reason };
}

/** Обёртка вокруг запроса: сетевая ошибка — состояние экрана, не исключение. */
export async function attempt<T>(
  what: string,
  load: () => Promise<T>,
): Promise<ReadResult<T>> {
  try {
    return { ok: true, value: await load() };
  } catch (error) {
    if (error instanceof ApiError) return failure(`${what}: ${error.message}`);
    return failure(`${what}: ${String(error)}`);
  }
}

/**
 * Потолок постраничного обхода реестра.
 *
 * Реестр района — это тысячи строк, и экран прототипа фильтрует их у себя.
 * Обходить страницы до конца можно только потому, что список ОГРАНИЧЕН
 * районом; потолок стоит здесь, чтобы «до конца» никогда не означало «пока
 * хватит памяти». Достигнутый потолок не молчит — он виден полю `truncated`.
 *
 * `PAGE_LIMIT` — ПРОСЬБА, а не обещание: сервер урезает страницу до своего
 * `MAX_LIMIT` (500, `backend/serving/api.py`). Шаг обхода поэтому берётся из
 * ответа (`page.limit`), а не из просьбы: прежний обход сдвигал смещение на
 * 1000 при страницах по 500 и молча терял каждую вторую страницу — реестр
 * контуров приходил 5 000 строками из 9 624.
 */
export const PAGE_LIMIT = 500;
export const PAGE_CAP = 20000;

export interface Paged<T> {
  items: T[];
  total: number;
  truncated: boolean;
}

interface Page<T> {
  items: T[];
  page: { total: number; limit?: number };
}

/**
 * Постраничный обход одного канонического списка.
 *
 * Первая страница называет итог и фактический размер страницы; остальные
 * запрашиваются разом, а не цепочкой: число запросов то же, ожидание — одной
 * страницы, а не двадцати подряд.
 */
export async function pageThrough<T>(
  load: (limit: number, offset: number) => Promise<Page<T>>,
): Promise<Paged<T>> {
  const first = await load(PAGE_LIMIT, 0);
  const total = first.page.total;
  const step = first.page.limit && first.page.limit > 0 ? first.page.limit : first.items.length;
  const items: T[] = [...first.items];
  if (step > 0 && items.length < total) {
    const offsets: number[] = [];
    for (let offset = step; offset < Math.min(total, PAGE_CAP); offset += step) {
      offsets.push(offset);
    }
    const rest = await Promise.all(offsets.map((offset) => load(PAGE_LIMIT, offset)));
    for (const chunk of rest) items.push(...chunk.items);
  }
  return { items, total, truncated: items.length < total };
}

/**
 * Память на время жизни источника.
 *
 * Один экран читает три-четыре набора, и реестр контуров под ними один и тот
 * же. Без общей памяти каждый хук заново обошёл бы все страницы: источник
 * пересоздаётся при смене КОНТЕКСТА анализа, поэтому ключ памяти — контекст
 * плюс имя набора, а не адрес экрана.
 */
const CACHE = new VersionedMemo<unknown>();

/**
 * Набор под версией данных (agroai-0e8.26, журнал №1638): на следующей
 * навигации и после записи он отдаётся, только если сервер снова подтвердил
 * версию, под которой набор собран. Неудача не запоминается.
 */
export function once<T>(key: string, load: () => Promise<T>): Promise<T> {
  return CACHE.get(key, load) as Promise<T>;
}

/** Ключ памяти: контекст анализа целиком, а не только дата. */
export function contextKey(params: QueryParams): string {
  return JSON.stringify(params);
}

/** Сброс памяти — нужен тестам, чтобы соседний случай не читал чужой ответ. */
export function resetDatasetCache(): void {
  CACHE.clear();
}

// Ответ срезан областью чтения вошедшего: сменился аккаунт — память не его.
onPrincipalChange(resetDatasetCache);

